import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { fmtIDR } from "@/lib/format";
import { Scale, CheckCircle2, AlertTriangle, Download } from "lucide-react";
import { API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { todayISO } from "@/lib/format";

const Row = ({ code, name, amount, indent = 0, strong, testid }) => (
  <div data-testid={testid} className={`flex items-center justify-between py-1.5 text-sm ${strong ? "font-bold border-t pt-2" : ""}`} style={{ paddingLeft: indent * 12 }}>
    <span className="flex items-center gap-2">
      {code && <span className="font-mono-fig text-xs text-slate-400 w-12">{code}</span>}
      <span className={strong ? "text-slate-900" : "text-slate-700"}>{name}</span>
    </span>
    <span className={`font-mono-fig ${strong ? "text-slate-900" : "text-slate-800"}`}>{fmtIDR(amount)}</span>
  </div>
);

const NeracaPage = () => {
  const { user } = useAuth();
  const [asOf, setAsOf] = useState(todayISO());
  const [data, setData] = useState(null);
  const [modal, setModal] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalInput, setModalInput] = useState("");

  const load = () => {
    api.get("/neraca", { params: { as_of: asOf } }).then((r) => setData(r.data));
    api.get("/settings/modal").then((r) => setModal(r.data.amount));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [asOf]);

  const saveModal = async (e) => {
    e.preventDefault();
    await api.put("/settings/modal", { amount: parseFloat(modalInput) });
    toast.success("Modal disetor diperbarui");
    setOpenModal(false); load();
  };

  if (!data) return <div className="text-slate-500">Memuat...</div>;

  return (
    <div className="space-y-6" data-testid="neraca-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900" data-testid="neraca-title">Neraca (Balance Sheet)</h1>
          <p className="text-sm text-slate-500 mt-1">Laporan Posisi Keuangan — SAK ETAP</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">Per Tanggal</Label><Input data-testid="neraca-as-of" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} /></div>
          <Button variant="outline" data-testid="neraca-pdf-button" onClick={() => {
            const token = localStorage.getItem("spbu_token");
            const outlet = localStorage.getItem("spbu_active_outlet") || "default";
            fetch(`${API}/export/neraca.pdf?as_of=${asOf}`, { headers: { Authorization: `Bearer ${token}`, "X-Outlet-Id": outlet } })
              .then(r => r.blob()).then(b => {
                const url = URL.createObjectURL(b);
                const a = document.createElement("a"); a.href = url; a.download = "neraca.pdf"; a.click();
              });
          }}><Download className="w-4 h-4 mr-2" />PDF</Button>
          <Button className="bg-[#005DAA] hover:bg-[#004885]" data-testid="neraca-excel-button" onClick={() => {
            const token = localStorage.getItem("spbu_token");
            const outlet = localStorage.getItem("spbu_active_outlet") || "default";
            fetch(`${API}/export/neraca.xlsx?as_of=${asOf}`, { headers: { Authorization: `Bearer ${token}`, "X-Outlet-Id": outlet } })
              .then(r => r.blob()).then(b => {
                const url = URL.createObjectURL(b);
                const a = document.createElement("a"); a.href = url; a.download = "neraca.xlsx"; a.click();
              });
          }}><Download className="w-4 h-4 mr-2" />Excel</Button>
          {user?.role === "admin" && (
            <Dialog open={openModal} onOpenChange={setOpenModal}>
              <DialogTrigger asChild><Button variant="outline" data-testid="neraca-modal-button">Set Modal Disetor</Button></DialogTrigger>
              <DialogContent>
                <DialogHeader><DialogTitle>Modal Disetor</DialogTitle></DialogHeader>
                <form onSubmit={saveModal} className="space-y-4">
                  <div><Label>Jumlah Modal (Rp)</Label>
                    <Input data-testid="neraca-modal-input" type="number" value={modalInput} placeholder={String(modal)} onChange={(e) => setModalInput(e.target.value)} required />
                  </div>
                  <DialogFooter><Button type="submit" data-testid="neraca-modal-submit" className="bg-[#E30613] hover:bg-[#C8040F]">Simpan</Button></DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card className={`border-2 ${data.balanced ? "border-emerald-300 bg-emerald-50/40" : "border-amber-300 bg-amber-50/40"}`}>
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {data.balanced ? <CheckCircle2 className="w-6 h-6 text-emerald-600" /> : <AlertTriangle className="w-6 h-6 text-amber-600" />}
            <div>
              <div className="font-bold text-sm" data-testid="neraca-balance-badge">{data.balanced ? "Neraca Seimbang" : "Neraca Tidak Seimbang"}</div>
              <div className="text-xs text-slate-500">Selisih: <span className="font-mono-fig" data-testid="neraca-difference">{fmtIDR(data.difference)}</span></div>
            </div>
          </div>
          <Scale className="w-8 h-8 text-slate-400" />
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="neraca-assets-panel">
          <CardHeader><CardTitle>ASET</CardTitle></CardHeader>
          <CardContent>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Aset Lancar</div>
            {data.assets.current.map((a) => <Row key={a.code} {...a} indent={1} />)}
            <Row name="Total Aset Lancar" amount={data.assets.total_current} strong />
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-4 mb-2">Aset Tetap</div>
            {data.assets.fixed.map((a) => <Row key={a.code} {...a} indent={1} />)}
            <Row name="Total Aset Tetap" amount={data.assets.total_fixed} strong />
            <div className="pt-3 mt-2 border-t-2 border-slate-800">
              <Row name="TOTAL ASET" amount={data.assets.total} strong testid="neraca-total-assets" />
            </div>
          </CardContent>
        </Card>

        <Card data-testid="neraca-liab-equity-panel">
          <CardHeader><CardTitle>LIABILITAS &amp; EKUITAS</CardTitle></CardHeader>
          <CardContent>
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">Liabilitas Jangka Pendek</div>
            {data.liabilities.current.map((a) => <Row key={a.code} {...a} indent={1} />)}
            <Row name="Total Liabilitas Jangka Pendek" amount={data.liabilities.total_current} strong />
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-4 mb-2">Liabilitas Jangka Panjang</div>
            {data.liabilities.non_current.map((a) => <Row key={a.code} {...a} indent={1} />)}
            <Row name="Total Liabilitas" amount={data.liabilities.total} strong />
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mt-4 mb-2">Ekuitas</div>
            {data.equity.items.map((a) => <Row key={a.code} {...a} indent={1} />)}
            <Row name="Total Ekuitas" amount={data.equity.total} strong />
            <div className="pt-3 mt-2 border-t-2 border-slate-800">
              <Row name="TOTAL LIABILITAS & EKUITAS" amount={data.liabilities.total + data.equity.total} strong testid="neraca-total-liab-equity" />
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default NeracaPage;
